import { useNavigate, useLocation, useSearchParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { Button, Stack, Typography, ButtonGroup, ButtonGroupItem } from "@financeable-com-au/financeable-ui";
import {
  AUTH_REQUEST_KEYS,
  authV2OrganisationSelect,
  authV2Selector,
  toAuthV2Error,
  toMfaEnrolmentStart,
  type AuthOrganisation,
} from "@/store/slices/authV2Slice";
import type { AppDispatch } from "@/store/store";
import { openSnackbar } from "@/store/slices/snackbarSlice";
import { AuthCard } from "@components/AuthCard";
import { MiniLoader } from "@/components/Loader";
import { buildPath } from "@/utils/buildPath";
import { completeAuthNavigation } from "@/utils/standaloneConnect";

const AuthSelectOrganisation = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const authV2 = useSelector(authV2Selector);
  const loading = Boolean(authV2?.loading?.[AUTH_REQUEST_KEYS.organisationSelect]);
  const organisations: AuthOrganisation[] = location.state?.organisation ?? [];
  const pendingAuthenticationToken: string = location.state?.pendingAuthenticationToken ?? "";
  const invitationToken = searchParams.get("invitationToken") ?? searchParams.get("invitation_token") ?? "";

  const navigateToSignIn = () => {
    navigate("/auth/sign-in");
  };

  const handleSelectOrganisation = async (id: string) => {
    const result = await dispatch(
      authV2OrganisationSelect({
        organisationId: id,
        pendingAuthenticationToken,
      }),
    );
    if (result.error) {
      const parsedError = toAuthV2Error(result.payload);

      // MFA enrolment required to proceed.
      const mfaEnrolmentStart = toMfaEnrolmentStart(result.payload);
      if (parsedError.code === "MFA_ENROLMENT_REQUIRED" && mfaEnrolmentStart) {
        navigate(
          buildPath("/auth/mfa-enrolment", {
            ...(invitationToken ? { invitationToken } : {}),
          }),
          {
            state: mfaEnrolmentStart,
          },
        );
        return;
      }

      dispatch(
        openSnackbar({
          message: parsedError.message,
          intent: "danger",
        }),
      );
      return;
    }
    if (invitationToken) {
      navigate(
        buildPath("/auth/invite/accept", {
          invitationToken,
        }),
      );
      return;
    }

    completeAuthNavigation(result.payload);
  };

  return (
    <>
      <Stack textAlign="center" rowGap={1}>
        <Typography level="title-lg">Select an organisation to continue</Typography>
        <Typography level="body-lg" textColor="neutral.600">
          Your account is linked to multiple organisations. Please select which one you'd like to sign in with.
        </Typography>
      </Stack>
      <AuthCard>
        {loading ? (
          <MiniLoader />
        ) : (
          <ButtonGroup orientation="vertical">
            {organisations.map(({ id, name }) => {
              return (
                <ButtonGroupItem
                  key={`${id}_${name}`}
                  endDecorator={<ChevronRightIcon />}
                  variant="secondary"
                  size="lg"
                  onClick={() => handleSelectOrganisation(id)}
                >
                  {name}
                </ButtonGroupItem>
              );
            })}
          </ButtonGroup>
        )}
      </AuthCard>
      <Button
        startDecorator={<ChevronLeftIcon sx={{ fontSize: "1.2rem" }} />}
        variant="inline"
        intent="neutral"
        size="lg"
        onClick={navigateToSignIn}
      >
        Go back
      </Button>
    </>
  );
};

export { AuthSelectOrganisation };
